import React, { memo } from "react";

import Spinner from "@src/components/atoms/spinner/spinner";
import { AlarmListHeader } from "@src/components/molecules/alarm-list-header/alarm-list-header";
import { AlarmListTable } from "@src/components/molecules/alarm-list-table/alarm-list-table";
import { AlarmListToolbar } from "@src/components/molecules/alarm-list-toolbar/alarm-list-toolbar";
import { AlarmNodeDetailsPanel } from "../alarm-node-details-panel/alarm-node-details-panel";
import { ButtonWithLoader } from "@src/components/atoms/buttons/button-with-loader/button-with-loader";
import { ContainerPane } from "../container-pane/container-pane";
import { ContainerPanel } from "../container-panel/container-panel";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { FluidToFixSplitContainer } from "@src/components/templates/layouts/fluid-to-fix-split-container/fluid-to-fix-split-container";
import { Spacer16 } from "@src/components/atoms/spacer-16/spacer-16";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import styles from "./alarm-list.module.scss";
import { ActiveAlarmProfileTitleAlarmList } from "@src/components/atoms/active-alarm-profile-title-alarm-list/active-alarm-profile-title-alarm-list";
import { useAlarmListNewHook } from "./use-alarm-list-new-hook";
import { useI18N } from "@src/language/language-context";
import { AlarmSearchbarComponent } from "./alarm-searchbar-component";

const cssClassName = "alarm-list";

export const AlarmList = memo(() => {
  const i18n = useI18N();

  const {
    alarmNodeSelectionState,
    arrayOfAlarmRows,
    handleSearchResults,
    showAlarmListRow,
    isSearchLoading,
    isEmptyAlarmsData,
    showNoSearchResults,
    showSearchBar,
    alarmListInstance,
    isTransferAlarmProfilesToClientActive,
    transferAlarmProfilesToClient,
    transferAlarmLoading,
  } = useAlarmListNewHook();

  const texts = {
    transferAlarmProfilesToClientButton: i18n(
      "modules.alarmList.transferAlarmProfilesToClientButton",
    ),
    transferAlarmProfilesToClientButtonTooltip: i18n(
      "modules.alarmList.transferAlarmProfilesToClientButtonTooltip",
    ),
    couldNotFindAnyNodesWithThatName: i18n(
      "modules.alarmList.couldNotFindAnyNodesWithThatName",
    ),
    noNodesFound: i18n("modules.alarmList.noNodesFound"),
  };

  return (
    <ContainerPanel>
      <FluidToFixSplitContainer noPadding>
        <div className={styles[cssClassName]}>
          <SubHeader>
            <FlexRowContainer justifyContent="space-between">
              <ActiveAlarmProfileTitleAlarmList />

              {isTransferAlarmProfilesToClientActive() && (
                <ButtonWithLoader
                  fun={transferAlarmProfilesToClient}
                  text={texts.transferAlarmProfilesToClientButton}
                  loading={transferAlarmLoading}
                />
              )}
            </FlexRowContainer>
          </SubHeader>
          <ContainerPane>
            <Spacer16 />
            {showSearchBar && (
              <div className={styles[`${cssClassName}__searchbar-wrapper`]}>
                <AlarmSearchbarComponent
                  onSearchMetaChange={handleSearchResults}
                />
              </div>
            )}
            {arrayOfAlarmRows !== undefined && (
              <>
                <AlarmListToolbar arrayOfAlarmRows={arrayOfAlarmRows} />
                <Spacer16 />
              </>
            )}
            {!isEmptyAlarmsData && alarmListInstance && (
              <AlarmListHeader alarmListInstance={alarmListInstance} />
            )}
            {showAlarmListRow === undefined || isSearchLoading ? (
              <div className={styles[`${cssClassName}__spinner-wrapper`]}>
                <Spinner />
              </div>
            ) : isEmptyAlarmsData ? (
              <div className={styles[`${cssClassName}__empty-wrapper`]}>
                <p className={styles[`${cssClassName}__empty-message`]}>
                  {showNoSearchResults
                    ? texts.couldNotFindAnyNodesWithThatName
                    : texts.noNodesFound}
                </p>
              </div>
            ) : (
              <AlarmListTable arrayOfAlarmRows={showAlarmListRow} />
            )}
          </ContainerPane>
        </div>
        {alarmNodeSelectionState === "show" && alarmListInstance && (
          <AlarmNodeDetailsPanel alarmListInstance={alarmListInstance} />
        )}
      </FluidToFixSplitContainer>
    </ContainerPanel>
  );
});

AlarmList.displayName = "AlarmList";
